import React, { ReactElement } from "react";
import {
  Column,
  StyledTable,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  formatValue,
  DataType,
  getNestedValue,
} from "./BaseTable";
import { filterColumns as filterRegularColumns } from "./RegularTable";
import { Check, Cross } from "./TableComponents";
import defaultTheme from "../../../styles/themes/default";

function isReactElement(value: any): value is React.ReactElement {
  return React.isValidElement(value);
}

interface TransposedRow {
  header: string | React.ReactElement;
  violation?: (value: any) => boolean;
  dataType?: DataType;
  highlight?: boolean;
  highlightColumn?: boolean;
  rowStyle?: string;
  [key: string]: any;
}

interface TransposedColumn {
  header: string | React.ReactElement;
  accessor:
    | keyof TransposedRow
    | ((row: any) => string[] | string | React.ReactElement);
  align: "left" | "center" | "right" | undefined;
  columnStyle?: string;
  highlight?: boolean;
  highlightColumn?: boolean;
  dataType?: DataType;
  columnClassName?: string;
  violation?: (value: any) => boolean;
}

interface TransposedTableProps<T = any> {
  id?: string;
  columns: Column<T>[];
  data: T[];
  filterNullAnd0Columns?: boolean;
  rotateHeaders?: boolean;
  rowLabelColumn?: keyof T;
  extraStyle?: string;
  showHeader?: boolean;
  highlightColumn?: boolean;
  autoAlignCurrency?: boolean;
  className?: string;
  appendEmptyCells?: boolean;
  showCrossOrCheck?: boolean;
  regularStyles?: boolean;
}

function AddHeaderRows(
  columns: TransposedColumn[],
  rotateHeaders = false,
  maxArrayLength: number
): ReactElement {
  const headerElements: React.ReactElement[] = [];

  for (let colIndex = 0; colIndex < columns.length; colIndex++) {
    const column = columns[colIndex];

    // Add the primary header cell for the current column
    headerElements.push(
      <TableHeaderCell
        key={`th_${colIndex}_main`}
        rotated={rotateHeaders}
        align={column.align ? column.align : colIndex === 0 ? "left" : "right"}
        extraStyle={`border-bottom: 1px dashed ${defaultTheme.colors.dashedBorder};`}
      >
        {rotateHeaders ? <div>{column.header}</div> : column.header}
      </TableHeaderCell>
    );

    // Add padding cells to make this column header group span maxArrayLength cells
    if (colIndex > 0 && maxArrayLength > 1) {
      for (let i = 1; i < maxArrayLength; i++) {
        headerElements.push(
          <TableHeaderCell
            key={`th_${colIndex}_pad_${i}`}
            rotated={rotateHeaders}
            align={column.align} // Align padding cells consistently
            extraStyle={`border-bottom: 1px dashed ${defaultTheme.colors.dashedBorder};`}
          >
            {/* Extra cells are intentionally empty */}
          </TableHeaderCell>
        );
      }
    }
  }

  return <tr>{headerElements}</tr>;
}

function AddDataRows(
  columns: TransposedColumn[],
  data: TransposedRow[],
  showHeader: boolean,
  highlightColumn: boolean,
  autoAlignCurrency: boolean,
  maxArrayLength: number,
  appendEmptyCells: boolean,
  showCrossOrCheck = false,
  regularStyles = false
): ReactElement {
  const displayData = data;

  return (
    <>
      {displayData.length > 0 &&
        displayData.map((row, rowIndex) => (
          <TableRow
            key={rowIndex}
            index={rowIndex}
            noBackground={regularStyles ? false : true}
          >
            {columns.map((col, colIndex) => {
              const value =
                typeof col.accessor === "function"
                  ? col.accessor(row)
                  : row[col.accessor as keyof TransposedRow];

              const shouldHighlight =
                colIndex === 0
                  ? highlightColumn || row.highlightColumn
                  : row.highlight;
              const cells: React.ReactElement[] = [];

              const isLastRow = rowIndex === displayData.length - 1;
              const borderStyle =
                isLastRow || regularStyles
                  ? ""
                  : `border-bottom: 1px dashed ${defaultTheme.colors.dashedBorder};`;
              const cellBaseStyle = `${row.rowStyle ?? ""} ${col.columnStyle ??
                ""} ${borderStyle}`;

              const cellBaseClassName = `${row.rowClassName ??
                ""} ${col.columnClassName ?? ""}`;

              // Determine the target number of cells for this logical column.
              // The first column (index 0) always gets 1 cell.
              // Other columns get maxArrayLength cells if padding is active.
              const targetCellCount =
                colIndex > 0 && maxArrayLength > 1 ? maxArrayLength : 1;

              const violation = row.violation;
              let isViolation = violation ? violation(value) : false;

              if (Array.isArray(value)) {
                // Determine how many items from the array to render based on appendEmptyCells
                const itemsToRenderCount = appendEmptyCells
                  ? Math.min(value.length, targetCellCount)
                  : value.length;

                for (
                  let itemIndex = 0;
                  itemIndex < itemsToRenderCount;
                  itemIndex++
                ) {
                  const val = value[itemIndex];

                  isViolation = violation ? violation(val) : false;

                  cells.push(
                    <TableCell
                      key={`${colIndex}-data-${itemIndex}`}
                      extraStyle={cellBaseStyle}
                      className={cellBaseClassName}
                      highlight={shouldHighlight}
                      violation={isViolation}
                      align={
                        colIndex > 0 &&
                        autoAlignCurrency &&
                        row.dataType === "currency"
                          ? "right"
                          : col.align
                      }
                    >
                      {getValue(
                        val,
                        colIndex === 0 ? undefined : row.dataType,
                        showCrossOrCheck
                      )}
                    </TableCell>
                  );
                }

                // Add padding cells only if appendEmptyCells is true and array was shorter than targetCellCount
                if (appendEmptyCells) {
                  for (let i = value.length; i < targetCellCount; i++) {
                    cells.push(
                      <TableCell
                        key={`${colIndex}-pad-${i}`}
                        extraStyle={cellBaseStyle}
                        className={cellBaseClassName}
                        highlight={false}
                        align={
                          colIndex > 0 &&
                          autoAlignCurrency &&
                          row.dataType === "currency"
                            ? "right"
                            : col.align
                        }
                      >
                        &nbsp;
                      </TableCell>
                    );
                  }
                }
              } else {
                cells.push(
                  <TableCell
                    key={`${colIndex}-data-0`}
                    extraStyle={cellBaseStyle}
                    className={cellBaseClassName}
                    highlight={shouldHighlight}
                    violation={colIndex > 0 ? isViolation : false}
                    align={getAlign(colIndex, autoAlignCurrency, row)}
                  >
                    {getValue(
                      value,
                      colIndex === 0 ? undefined : row.dataType,
                      showCrossOrCheck
                    )}
                  </TableCell>
                );

                // Add padding cells only if appendEmptyCells is true and targetCellCount > 1
                if (appendEmptyCells) {
                  for (let i = 1; i < targetCellCount; i++) {
                    cells.push(
                      <TableCell
                        key={`${colIndex}-pad-${i}`}
                        extraStyle={cellBaseStyle}
                        className={cellBaseClassName}
                        highlight={false}
                        align={getAlign(colIndex, autoAlignCurrency, row)}
                      >
                        &nbsp;
                      </TableCell>
                    );
                  }
                }
              }
              return cells;
            })}
          </TableRow>
        ))}
    </>
  );
}

function getValueWithCrossOrCheck(
  value: any,
  dataType?: DataType
): ReactElement | string {
  if (
    value &&
    typeof value === "object" &&
    "value" in value &&
    "isOk" in value
  ) {
    return (
      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <span style={{ textAlign: "right", flex: 1 }}>
          {formatValue(value.value, dataType)}
        </span>
        <span
          style={{ width: "28px", display: "flex", justifyContent: "center" }}
        >
          {value.isOk ? <Check /> : <Cross />}
        </span>
      </span>
    );
  }

  if (
    dataType === "currency" ||
    dataType === "currency-nillable" ||
    dataType === "currency-with-decimals"
  ) {
    return (
      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <span style={{ textAlign: "right", flex: 1 }}>
          {formatValue(value, dataType)}
        </span>
        <span style={{ width: "28px" }}></span>
      </span>
    );
  }

  if (value === true && dataType === "boolean") {
    return <Check />;
  }

  if (value === false && dataType === "boolean") {
    return <Cross />;
  }

  return formatValue(value, dataType);
}

function getValue(
  value: any,
  dataType?: DataType,
  showCrossOrCheck = false
): ReactElement | string {
  if (showCrossOrCheck) {
    return getValueWithCrossOrCheck(value, dataType);
  }

  if (value === true && dataType === "boolean") {
    return <Check />;
  }

  if (value === false && dataType === "boolean") {
    return <Cross />;
  }

  return formatValue(value, dataType);
}

function getAlign(
  colIndex: number,
  autoAlignCurrency: boolean,
  row: TransposedRow
): "left" | "center" | "right" {
  if (colIndex <= 0) {
    return "left";
  }

  return colIndex > 0 && autoAlignCurrency && row.dataType === "currency"
    ? "right"
    : row.align ?? "right";
}

/**
 * Transposes the table data - converts rows to columns and columns to rows
 */
function transposeTable<T extends Record<string, any>>(
  columns: Column<T>[],
  data: T[],
  rowLabelColumn?: keyof T
): { columns: TransposedColumn[]; data: TransposedRow[] } {
  // Determine which column to use for row labels
  const labelColumnIndex = rowLabelColumn
    ? columns.findIndex((col) => col.accessor === rowLabelColumn)
    : 0;

  const labelColumn = columns[labelColumnIndex >= 0 ? labelColumnIndex : 0];

  // Create new columns from the data rows
  const newColumns: TransposedColumn[] = data.map((row, index) => {
    // Try to get the value from the label column
    const labelValue =
      typeof labelColumn.accessor === "function"
        ? labelColumn.accessor(row)
        : row[labelColumn.accessor as keyof T]?.toString();

    // If no label value, create a descriptive fallback using the first non-label column's header
    const fallbackLabel =
      labelValue ??
      (() => {
        const firstDataColumn = columns.find(
          (col, idx) => idx !== labelColumnIndex
        );
        return firstDataColumn
          ? `${firstDataColumn.header} - Row ${index + 1}`
          : `Row ${index + 1}`;
      })();

    return {
      header: fallbackLabel,
      accessor: (row: TransposedRow) => row[`row_${index}`],
      align: labelColumn.align ?? "right",
      columnStyle: columns[0]?.style,
      columnClassName: columns[0]?.className,
      highlight: columns[0]?.highlight,
      highlightColumn: columns[0]?.highlightColumn,
      violation: columns[0]?.violation,
    };
  });

  // Add a column for the original headers
  newColumns.unshift({
    header: isReactElement(labelColumn.header)
      ? labelColumn.header
      : String(labelColumn.header || ""),
    accessor: (row: TransposedRow) => row.header,
    align: "left",
    columnStyle: labelColumn.style,
    columnClassName: labelColumn.className,
    highlight: labelColumn.highlight,
    highlightColumn: labelColumn.highlightColumn,
  });

  // Create new data from the columns, excluding the label column
  const newData: TransposedRow[] = columns
    .filter((col, idx) => idx !== labelColumnIndex)
    .map((col, colIndex) => {
      const newRow: TransposedRow = {
        header: col.header,
        dataType: col.dataType,
        violation: col.violation,
        highlight: col.highlight,
        highlightColumn: col.highlightColumn,
        rowStyle: col.style,
        rowClassName: col.className,
        align: col.align,
      };

      // Add data from each original row for this column
      data.forEach((row, rowIndex) => {
        if (typeof col.accessor === "function") {
          newRow[`row_${rowIndex}`] = col.accessor(row);
        } else if (typeof col.accessor === "string") {
          newRow[`row_${rowIndex}`] = getNestedValue(
            row,
            col.accessor as string
          );
        } else {
          // Fallback for non-string/non-function accessors (e.g. direct keyof T which might be a symbol or number)
          // For safety, one might want to ensure col.accessor is a string before calling getNestedValue if keyof T can be other types.
          // However, typical usage involves string keys for dot notation.
          newRow[`row_${rowIndex}`] = row[col.accessor as keyof T];
        }
      });

      return newRow;
    });

  return { columns: newColumns, data: newData };
}

const TransposedTable = <T extends Record<string, any>>({
  id,
  columns,
  data,
  filterNullAnd0Columns = true,
  rotateHeaders = false,
  rowLabelColumn,
  extraStyle,
  showHeader = true,
  highlightColumn = false,
  autoAlignCurrency = false,
  className,
  appendEmptyCells = false,
  showCrossOrCheck = false,
  regularStyles = false,
}: TransposedTableProps<T>): ReactElement => {
  if (!columns || columns.length === 0 || !data || data.length === 0) {
    throw new Error("columns and/or data can't be null, undefined or empty");
  }

  // Filter original columns *before* transposing
  const initialColumnsToUse = filterRegularColumns(
    columns,
    data,
    filterNullAnd0Columns
  ); // Use the filtering logic from RegularTable

  // If all columns are filtered out, render nothing
  if (initialColumnsToUse.length === 0) {
    return <></>;
  }

  const { columns: transposedColumns, data: transposedData } = transposeTable(
    initialColumnsToUse,
    data,
    rowLabelColumn
  );

  let maxArrayLength = 1;
  if (
    appendEmptyCells &&
    transposedData.length > 0 &&
    transposedColumns.length > 0
  ) {
    for (const dRow of transposedData) {
      for (const tCol of transposedColumns) {
        const cellValue =
          typeof tCol.accessor === "function"
            ? tCol.accessor(dRow)
            : dRow[tCol.accessor as keyof TransposedRow];

        if (Array.isArray(cellValue) && cellValue.length > maxArrayLength) {
          maxArrayLength = cellValue.length;
        }
      }
    }
  }

  return (
    <StyledTable
      id={id}
      data-testid={id}
      extraStyle={extraStyle}
      className={`transposed-table ${className || ""}`}
      rotate={true}
      regularStyles={regularStyles}
    >
      {showHeader && (
        <TableHeader>
          {AddHeaderRows(transposedColumns, rotateHeaders, maxArrayLength)}
        </TableHeader>
      )}
      <tbody>
        {AddDataRows(
          transposedColumns,
          transposedData,
          showHeader,
          highlightColumn,
          autoAlignCurrency,
          maxArrayLength,
          appendEmptyCells,
          showCrossOrCheck,
          regularStyles
        )}
        {showCrossOrCheck && (
          <TableRow
            key="cross-or-check-footer"
            noBackground={true}
            index={transposedData.length + 1}
          >
            <TableCell colSpan={transposedColumns.length}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                <span
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <Check /> voldoet aan de norm
                </span>
                <span
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <Cross /> overschrijdt de norm
                </span>
              </div>
            </TableCell>
          </TableRow>
        )}
      </tbody>
    </StyledTable>
  );
};

export default TransposedTable;
